import { categoriesKeys } from "@/features/inventory/queryKeys";
import type { GenericItem } from "@/types/api";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { categoriesApi } from "./api";

interface Props {
  open: boolean;
  initial: Partial<GenericItem> | null;
  onClose: () => void;
  onSave: (item: Partial<GenericItem>) => void;
}

const QUANTITIES = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12];

export function PlaceholderDialog({ open, initial, onClose, onSave }: Props) {
  const [name, setName] = useState("");
  const [category, setCategory] = useState<string>("");
  const [quantity, setQuantity] = useState(1);

  const isEditing = !!initial?.id;

  const categories = useQuery({
    queryKey: categoriesKeys.all,
    queryFn: categoriesApi.list,
  });

  useEffect(() => {
    setName(initial?.name ?? "");
    setCategory(initial?.furnitureCategory ? String(initial.furnitureCategory) : "");
    setQuantity(initial?.quantity ?? 1);
  }, [initial, open]);

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="xs">
      <DialogTitle>{isEditing ? "Edit placeholder" : "Add a placeholder"}</DialogTitle>
      <DialogContent>
        <Typography variant="body2" sx={{ mb: 1 }}>
          {isEditing
            ? "How would you like to rename your placeholder?"
            : "How would you like to name your new placeholder?"}
        </Typography>
        <TextField
          fullWidth
          required
          margin="dense"
          label="Item name"
          value={name}
          onChange={(e) => setName(e.target.value)}
        />
        <TextField
          fullWidth
          select
          margin="dense"
          label="Category"
          value={category}
          onChange={(e) => setCategory(e.target.value)}
        >
          <MenuItem value="">—</MenuItem>
          {(categories.data ?? []).map((cat) => (
            <MenuItem key={cat.id} value={String(cat.id)}>
              {cat.name}
            </MenuItem>
          ))}
        </TextField>
        <Typography variant="body2" sx={{ mt: 2 }}>
          Select a quantity below
        </Typography>
        <TextField
          fullWidth
          type="number"
          margin="dense"
          label="Quantity"
          value={quantity}
          onChange={(e) => setQuantity(Math.max(1, Number(e.target.value)))}
        />
        <Typography variant="body2" sx={{ mt: 1, mb: 1 }}>
          or choose between the quantities below
        </Typography>
        <Box sx={{ display: "grid", gridTemplateColumns: "repeat(6, 1fr)", gap: 1 }}>
          {QUANTITIES.map((q) => (
            <IconButton
              key={q}
              onClick={() => setQuantity(q)}
              sx={{
                bgcolor: q === quantity ? "primary.main" : "grey.100",
                color: q === quantity ? "primary.contrastText" : "primary.main",
                borderRadius: 1,
                "&:hover": { bgcolor: q === quantity ? "primary.dark" : "grey.200" },
              }}
            >
              {q}
            </IconButton>
          ))}
        </Box>
        <Stack direction="row" spacing={1} sx={{ mt: 2 }}>
          <Typography variant="caption" color="text.secondary">
            Selected: {quantity}
          </Typography>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          variant="contained"
          disabled={!name.trim()}
          onClick={() =>
            onSave({
              ...initial,
              name: name.trim(),
              quantity,
              furnitureCategory: category ? Number(category) : null,
            })}
        >
          {isEditing ? "Save" : "Create"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
